import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  IconButton,
  TextField,
  Typography,
  Button,
  Fab,
  Fade,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as SparklesIcon,
  ChatBubbleOutline as ChatIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { colors } from '../theme/theme';
import { askAdminAssistant, type AssistantAction } from '../api/assistant';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  actions?: AssistantAction[];
  typing?: boolean;
}

const WELCOME =
  "Hi — I'm K-Chat AI, your dashboard assistant. Ask me about your county's stats (revenue, transactions, applications, services) or how to find and do things in the dashboard.";

const quickQuestions = [
  'How much did we collect this week?',
  'How many applications are pending?',
  'Where do I edit FAQs?',
  'How do I approve an application?',
];

interface KChatAIProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const KChatAI = ({ isOpen, onToggle }: KChatAIProps) => {
  const [messages, setMessages] = useState<Message[]>([
    { id: '1', type: 'ai', content: WELCOME, timestamp: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || busy) return;

    const userMessage: Message = {
      id: Date.now().toString(), type: 'user', content: text, timestamp: new Date(),
    };
    const typingMessage: Message = {
      id: (Date.now() + 1).toString(), type: 'ai', content: '', timestamp: new Date(), typing: true,
    };
    setMessages(prev => [...prev, userMessage, typingMessage]);
    setInput('');
    setBusy(true);

    // Real backend: stats are computed server-side from this county's data and
    // returned as text (never sent to the LLM); navigation questions hit the LLM.
    const reply = await askAdminAssistant(text);
    const finalText = reply.answer ?? "I couldn't answer that just now — try a dashboard section, or rephrase your question.";
    const id = typingMessage.id;

    // Typewriter reveal — the answer arrives whole, but we surface it word-by-word
    // so it FEELS live. Respects reduced-motion (shows instantly). Actions appear
    // only once the text has finished revealing.
    await typewriterReveal(finalText, (partial, done) => {
      setMessages(prev => prev.map(m =>
        m.id === id
          ? { ...m, typing: false, content: partial, actions: done ? reply.actions : undefined }
          : m,
      ));
    });

    setBusy(false);
  };

  if (!isOpen) {
    return (
      <Fab
        onClick={onToggle}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 32,
          background: colors.gradients.primary,
          boxShadow: colors.shadows.vibrant,
          width: 64,
          height: 64,
          '&:hover': {
            background: colors.gradients.primaryHover,
            boxShadow: colors.shadows.vibrantHover,
            transform: 'scale(1.05)',
          },
          transition: 'all 0.3s ease',
        }}
        aria-label="Open K-Chat AI Assistant"
      >
        <SparklesIcon sx={{ fontSize: 32, color: 'white' }} />
      </Fab>
    );
  }

  return (
    <Fade in={isOpen}>
      <Card
        sx={{
          position: 'fixed',
          bottom: 96,
          right: 32,
          width: 420,
          height: 620,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: colors.shadows.xl,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: colors.gradients.primary,
            color: 'white',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <SparklesIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.2 }}>
                K-Chat AI
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                Your K-Pay Admin Assistant
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onToggle}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            <CloseIcon sx={{ color: 'white', fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2.5,
            background: `linear-gradient(to bottom, ${colors.grey[50]}, white)`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: message.type === 'user' ? 'flex-end' : 'flex-start',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  maxWidth: '85%',
                  borderRadius: 3,
                  px: 2,
                  py: 1.5,
                  boxShadow: colors.shadows.sm,
                  ...(message.type === 'user'
                    ? {
                        background: colors.gradients.primary,
                        color: 'white',
                      }
                    : {
                        bgcolor: 'white',
                        border: `1px solid ${colors.grey[200]}`,
                        color: colors.grey[800],
                      }),
                }}
              >
                {message.typing ? (
                  <CircularProgress size={14} sx={{ color: colors.primary.main }} />
                ) : (
                  <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                    {renderRich(message.content)}
                  </Typography>
                )}
              </Box>

              {/* Action deep-links — navigate within the dashboard. */}
              {!message.typing && message.actions && message.actions.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {message.actions.map((a) => (
                    <Chip
                      key={a.to + a.label}
                      label={a.label}
                      size="small"
                      onClick={() => { navigate(a.to); onToggle(); }}
                      sx={{
                        bgcolor: colors.primary[50],
                        color: colors.primary.dark,
                        fontWeight: 600,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: colors.primary[100] },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Box>

        {/* Quick Questions */}
        {messages.length === 1 && (
          <Box
            sx={{
              px: 2.5,
              pb: 1.5,
              bgcolor: 'white',
              borderTop: `1px solid ${colors.grey[100]}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.grey[500],
                fontSize: '0.75rem',
                mb: 1.5,
                display: 'block',
              }}
            >
              Quick questions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  onClick={() => handleSend(question)}
                  sx={{
                    fontSize: '0.75rem',
                    bgcolor: colors.grey[100],
                    color: colors.grey[700],
                    '&:hover': {
                      bgcolor: colors.grey[200],
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Input */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${colors.grey[200]}`,
            bgcolor: 'white',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask me anything..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  bgcolor: colors.grey[50],
                  '& fieldset': {
                    borderColor: colors.grey[200],
                  },
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                    borderWidth: 2,
                  },
                },
              }}
            />
            <IconButton
              onClick={() => handleSend()}
              sx={{
                width: 44,
                height: 44,
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <SendIcon sx={{ color: 'white', fontSize: 20 }} />
            </IconButton>
          </Box>
        </Box>
      </Card>
    </Fade>
  );
};

/**
 * Reveal `text` word-by-word via `onTick(partialText, done)`. Pure frontend
 * effect — the answer is already fully received. Respects reduced-motion (shows
 * the whole answer at once). Caps total time so long answers don't crawl.
 */
function typewriterReveal(text: string, onTick: (partial: string, done: boolean) => void): Promise<void> {
  const prefersReduced = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced || !text) {
    onTick(text, true);
    return Promise.resolve();
  }

  const tokens = text.split(/(\s+)/); // keep whitespace so spacing is preserved
  // Aim for ~1.2s total reveal, but never slower than 12ms/token or faster than 40ms.
  const stepMs = Math.min(40, Math.max(12, Math.round(1200 / Math.max(tokens.length, 1))));

  return new Promise((resolve) => {
    let i = 0;
    let acc = '';
    const tick = () => {
      acc += tokens[i] ?? '';
      i += 1;
      const done = i >= tokens.length;
      onTick(acc, done);
      if (done) { resolve(); return; }
      window.setTimeout(tick, stepMs);
    };
    tick();
  });
}

/** Render *bold* spans; newlines handled by whiteSpace: pre-line. */
function renderRich(text: string): React.ReactNode {
  return text.split(/(\*[^*]+\*)/g).map((part, i) =>
    /^\*[^*]+\*$/.test(part)
      ? <strong key={i}>{part.slice(1, -1)}</strong>
      : <span key={i}>{part}</span>,
  );
}
